<template>
    <b-modal v-model="visible" size="lg" title="Select Location" @shown="initMap" @hidden="handleClose">
        <div id="map" style="height: 400px; width: 100%;"></div>

        <template #modal-footer>
            <b-button class="btn btn-size akkurate-danger-bg text-white" @click="close">
                Cancel
            </b-button>
            <b-button class="btn btn-size akkurate-green-bg" @click="confirm">
                Use Location
            </b-button>
        </template>

        <!-- Errors -->
        <b-alert v-if="errmsgs" show variant="danger" class="mt-2">
            <ul>
                <li v-for="(err, index) in errmsgs" :key="index">{{ err }}</li>
            </ul>
        </b-alert>
    </b-modal>
</template>

<script>
import { loadGoogleMapsScript } from "@/utils/googleMapsLoader";

export default {
    props: {
        value: { type: Boolean, default: false },
        initialCoords: { type: Object, default: null }
    },
    data() {
        return {
            visible: this.value,
            map: null,
            marker: null,
            selectedLatLng: null,
            selectedAddress: null,
            geocoder: null,
            loading: false,
            errmsgs: null
        };
    },
    watch: {
        value(val) {
            this.visible = val;
        },
        visible(val) {
            this.$emit("input", val);
        }
    },
    methods: {
        async initMap() {
            this.loading = true;
            try {
                await loadGoogleMapsScript();

                // If map already exists, clear listeners + reset
                if (this.map) {
                    window.google.maps.event.clearListeners(this.map, "click");
                } else {
                    this.map = new window.google.maps.Map(
                        document.getElementById("map"),
                        {
                            center: { lat: 5.6037, lng: -0.1870 }, // Accra
                            zoom: 12,
                            restriction: {
                                latLngBounds: {
                                    north: 11.2,
                                    south: 4.5,
                                    east: 1.2,
                                    west: -3.5
                                },
                                strictBounds: false
                            }
                        }
                    );
                }

                this.geocoder = new window.google.maps.Geocoder();

                // If initial coords exist
                if (
                    this.initialCoords &&
                    typeof this.initialCoords.lat === "number" &&
                    typeof this.initialCoords.lng === "number"
                ) {
                    this.map.setCenter(this.initialCoords);
                    this.setMarker(this.initialCoords, "Previously Selected Location");
                    this.selectedLatLng = this.initialCoords;
                    this.reverseGeocode(this.initialCoords);
                }

                // Listen for map clicks
                this.map.addListener("click", (event) => {
                    this.selectedLatLng = {
                        lat: event.latLng.lat(),
                        lng: event.latLng.lng()
                    };
                    this.setMarker(this.selectedLatLng, "Selected Location");
                    this.reverseGeocode(this.selectedLatLng);
                });

                this.loading = false;
            } catch (err) {
                this.loading = false;
                this.errmsgs = ["Google Maps failed to load"];
                console.error("Google Maps failed to load:", err);
            }
        },

        setMarker(position, title) {
            if (this.marker) {
                this.marker.setMap(null);
            }

            if (
                window.google.maps.marker &&
                window.google.maps.marker.AdvancedMarkerElement
            ) {
                this.marker = new window.google.maps.marker.AdvancedMarkerElement({
                    position,
                    map: this.map,
                    title
                });
            } else {
                this.marker = new window.google.maps.Marker({
                    position,
                    map: this.map,
                    title
                });
            }
        },

        reverseGeocode(latlng) {
            this.geocoder.geocode(
                { location: latlng, componentRestrictions: { country: "GH" } },
                (results, status) => {
                    if (status === "OK" && results[0]) {
                        let plusCode = null;

                        // ✅ plus_code is at top-level, not in results[0]
                        if (results.plus_code?.compound_code) {
                            plusCode = results.plus_code.compound_code
                                .split(" ")
                                .slice(0, 2)
                                .join(" ");
                        } else if (results.plus_code?.global_code) {
                            plusCode = results.plus_code.global_code;
                        }

                        // Fallback to formatted address
                        this.selectedAddress = plusCode || results[0].formatted_address;

                        const infowindow = new window.google.maps.InfoWindow({
                            content: `<strong>${this.selectedAddress}</strong><br>
                        Lat: ${latlng.lat}, Lng: ${latlng.lng}`
                        });
                        infowindow.open(this.map, this.marker);
                    } else {
                        this.selectedAddress = null;
                        this.errmsgs = ["Could not fetch address in Ghana"];
                    }
                }
            );
        },

        close() {
            this.visible = false;
            this.selectedLatLng = null;
            this.selectedAddress = null;
            if (this.marker) {
                this.marker.setMap(null);
                this.marker = null;
            }
        },

        confirm() {
            if (this.selectedAddress && this.selectedLatLng) {
                this.selectedAddress = this.selectedAddress + ` (Lat: ${this.selectedLatLng.lat}, Lng: ${this.selectedLatLng.lng})`;
                this.$emit("selected", this.selectedAddress);
                this.$emit("coords", { ...this.selectedLatLng });
                this.visible = false;
            } else {
                this.errmsgs = ["Please select a location first"];
            }
        },

        handleClose() {
            this.$emit("closed");
        }
    }
};
</script>
